import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import detectEthereumProvider from '@metamask/detect-provider'
import { ethers } from 'ethers'

export const useWeb3Store = defineStore('web3', () => {
  // State
  const isConnected = ref(false)
  const isConnecting = ref(false)
  const address = ref<string | null>(null)
  const chainId = ref<number | null>(null)
  const balance = ref<string>('0')
  const provider = ref<any>(null)
  const signer = ref<any>(null)
  const showConnectionModal = ref(false)

  // Getters
  const isAdmin = computed(() => {
    const adminAddresses = import.meta.env.VITE_ADMIN_ADDRESSES?.split(',') || []
    return address.value && adminAddresses.includes(address.value.toLowerCase())
  })

  const shortAddress = computed(() => {
    if (!address.value) return ''
    return `${address.value.slice(0, 6)}...${address.value.slice(-4)}`
  })

  // Actions
  async function tryAutoConnect() {
    try {
      const ethereum = await detectEthereumProvider()
      if (ethereum && ethereum.isMetaMask) {
        const accounts = await ethereum.request({ method: 'eth_accounts' })
        if (accounts.length > 0) {
          await connectWallet()
        }
      }
    } catch (error) {
      console.error('Auto-connect failed:', error)
    }
  }

  async function connectWallet() {
    try {
      isConnecting.value = true
      const ethereum = await detectEthereumProvider()
      
      if (!ethereum) {
        throw new Error('MetaMask not found')
      }

      const accounts = await ethereum.request({ 
        method: 'eth_requestAccounts' 
      })
      
      const ethersProvider = new ethers.BrowserProvider(ethereum)
      const ethersChainId = await ethersProvider.getNetwork()
      const ethersSigner = await ethersProvider.getSigner()
      const ethersBalance = await ethersProvider.getBalance(accounts[0])

      provider.value = ethersProvider
      signer.value = ethersSigner
      address.value = accounts[0]
      chainId.value = Number(ethersChainId.chainId)
      balance.value = ethers.formatEther(ethersBalance)
      isConnected.value = true
      showConnectionModal.value = false

      // Set up event listeners
      ethereum.on('accountsChanged', handleAccountsChanged)
      ethereum.on('chainChanged', handleChainChanged)
      
    } catch (error) {
      console.error('Wallet connection failed:', error)
      throw error
    } finally {
      isConnecting.value = false
    }
  }

  function disconnect() {
    address.value = null
    chainId.value = null
    balance.value = '0'
    provider.value = null
    signer.value = null
    isConnected.value = false
  }

  function handleAccountsChanged(accounts: string[]) {
    if (accounts.length === 0) {
      disconnect()
    } else if (accounts[0] !== address.value) {
      address.value = accounts[0]
      // Reload user data
    }
  }

  function handleChainChanged(newChainId: string) {
    chainId.value = parseInt(newChainId, 16)
    // Handle network change
  }

  return {
    // State
    isConnected,
    isConnecting,
    address,
    chainId,
    balance,
    provider,
    signer,
    showConnectionModal,
    
    // Getters
    isAdmin,
    shortAddress,
    
    // Actions
    tryAutoConnect,
    connectWallet,
    disconnect
  }
})